import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCachedSessions, getCachedChildren, getCachedDropdownValues, getCachedSession } from './offlineDB';

// Custom error class for network/offline errors
export class NetworkError extends Error {
  constructor(message, originalError, cachedData = null) {
    super(message);
    this.name = 'NetworkError';
    this.isNetworkError = true;
    this.isOffline = !navigator.onLine;
    this.originalError = originalError;
    this.cachedData = cachedData; // Include cached data if available
  }
}

// Store reference to OfflineContext updater (set by OfflineContext on initialization)
let offlineContextUpdater = null;

export const setOfflineContextUpdater = (updater) => {
  offlineContextUpdater = updater;
};

// URL patterns to their IndexedDB cache fallback functions
const OFFLINE_FALLBACK_MAP = {
  // Calendar/Sessions endpoints
  '/api/therapist/sessions': async () => {
    const sessions = await getCachedSessions();
    return { data: sessions, fromCache: true };
  },
  '/api/therapist/calendar': async () => {
    const sessions = await getCachedSessions();
    return { data: sessions, fromCache: true };
  },
  // Children endpoints
  '/api/therapist/children': async () => {
    const children = await getCachedChildren();
    return { data: children, fromCache: true };
  },
  '/api/children': async () => {
    const children = await getCachedChildren();
    return { data: children, fromCache: true };
  }
};

// Try to get cached data for a URL
const getCachedDataForUrl = async (url, config) => {
  try {
    // Check direct URL match first
    for (const [pattern, fallbackFn] of Object.entries(OFFLINE_FALLBACK_MAP)) {
      if (url.includes(pattern)) {
        const result = await fallbackFn();
        return result;
      }
    }

    // Check for session detail endpoint (e.g., /api/therapist/sessions/123)
    const sessionMatch = url.match(/\/api\/therapist\/sessions\/(\d+)/);
    if (sessionMatch) {
      const sessionId = parseInt(sessionMatch[1], 10);
      const session = await getCachedSession(sessionId);
      if (session) {
        return { data: session, fromCache: true };
      }
    }

    // Check for dropdown values
    const dropdownMatch = url.match(/\/api\/dropdown\/(.+)/);
    if (dropdownMatch) {
      const dropdownKey = dropdownMatch[1];
      const values = await getCachedDropdownValues(dropdownKey);
      if (values) {
        return { data: values, fromCache: true };
      }
    }

    return null;
  } catch (error) {
    console.error('[Offline] Error getting cached data:', error);
    return null;
  }
};

// Resolve the API base URL in a way that works in BOTH local dev and any
// deployed environment:
//   1. If VITE_APP_API_URL is provided at build time, always use it. Set this
//      when the API lives on a different origin than the web app
//      (e.g. https://api.ayro.com).
//   2. Otherwise, in local dev the backend runs on :3010 alongside the :3000
//      dev server, so default to that.
//   3. Otherwise (production build with no explicit URL), default to the page's
//      OWN origin. This is the common deployment shape — the API is served
//      behind the same host (reverse proxy routing /api/v1 → backend) — so
//      requests stay same-origin (no CORS) and work even if the build forgot
//      to set VITE_APP_API_URL. Previously this fell back to
//      'http://localhost:3010/', which made every API call (incl. saves) fail
//      in any deployed build that didn't bake in the env var.
const resolveApiBaseUrl = () => {
  const fromEnv = import.meta.env.VITE_APP_API_URL;
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return 'http://localhost:3010';
  return typeof window !== 'undefined' ? window.location.origin : '';
};

const axiosServices = axios.create({
  baseURL: resolveApiBaseUrl(),
  // 30s timeout — the backend auth path can take 5-10s in dev (argon2 + DB
  // cold-start). Keeping a timeout so genuinely offline requests still fail
  // fast rather than hang, but not so tight that healthy-but-slow requests
  // surface as "Unable to reach the server".
  timeout: 30000
});

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('serviceToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response) => {
    // Notify OfflineContext of successful request (network is working)
    if (offlineContextUpdater) {
      offlineContextUpdater(true);
    }
    return response;
  },
  async (error) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      // This happens when:
      // 1. Network is completely unavailable (WiFi off, no internet)
      // 2. Request timeout
      // 3. CORS errors
      // 4. Server is unreachable
      // NOTE: We do NOT set offline here. API failures can happen for many reasons
      // (auth, timeout, server restart) that don't mean the network is down.
      // The dedicated connectivity check in OfflineContext handles offline detection.
      console.warn('Network error detected:', error.message);

      // Try to get cached data for GET requests
      const config = error.config;
      if (config && config.method === 'get') {
        const cachedResult = await getCachedDataForUrl(config.url, config);
        if (cachedResult) {
          // Return cached data as a successful response
          return {
            data: cachedResult.data,
            status: 200,
            statusText: 'OK (Cached)',
            headers: {},
            config: config,
            fromCache: true
          };
        }
      }

      const networkError = new NetworkError(navigator.onLine ? 'Unable to reach the server' : 'You are offline', error);
      return Promise.reject(networkError);
    }

    // Handle HTTP error responses (server responded with error status)
    // Notify OfflineContext of successful network (server responded, even with error)
    if (offlineContextUpdater) {
      offlineContextUpdater(true);
    }

    // Handle 401 — try a silent refresh first so an expired access token
    // doesn't kick the user to /login and lose their form data. Only
    // redirect to login if refresh itself fails (or no refresh token).
    // The proactive timer below should make this path rare in practice.
    const originalRequest = error.config;
    const onAuthPage = window.location.href.includes('/login') || window.location.href.includes('/offline');
    const isAuthCall = originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/refresh');

    if (error.response.status === 401 && !onAuthPage && !isAuthCall && !originalRequest._retriedAfterRefresh) {
      let idleTimedOut = false;
      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest._retriedAfterRefresh = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosServices(originalRequest);
        }
      } catch (e) {
        // Distinguish a rolling-timeout refusal from a generic refresh failure
        // so the user sees the right message on /login.
        const refusalCode = e?.response?.data?.detail?.code;
        if (refusalCode === 'session_idle_timeout') {
          idleTimedOut = true;
        } else {
          console.warn('Silent token refresh failed:', e?.message);
        }
      }
      // AN EXPIRED IMPERSONATION IS NOT AN EXPIRED SESSION.
      //
      // Impersonation tokens are short-lived and deliberately have no refresh,
      // so reaching here is the NORMAL end of one, not a failure. The actor is
      // still signed in — their refresh token is parked — so send them back to
      // their own session rather than to the login screen. Without this, the
      // administrator's own working session was collateral damage every time an
      // impersonation timed out.
      if (!idleTimedOut && isImpersonationToken()) {
        const parked = localStorage.getItem(PARKED_REFRESH_KEY);
        if (parked) {
          localStorage.setItem('refreshToken', parked);
          localStorage.removeItem(PARKED_REFRESH_KEY);
          try {
            const actorToken = await refreshAccessToken();
            if (actorToken) {
              // Full reload for the same reason starting one reloads: nothing
              // cached as the target may survive into the actor's session.
              window.location.assign('/?impersonation=expired');
              return Promise.reject(error);
            }
          } catch (e) {
            console.warn('Could not restore the actor session after impersonation expiry:', e?.message);
          }
        }
      }

      // Refresh unavailable or failed → session is really over. Cancel any
      // pending proactive-refresh timer so it doesn't fire after logout.
      cancelProactiveRefresh();
      localStorage.removeItem('serviceToken');
      localStorage.removeItem('refreshToken');
      // Never leave a parked token behind for whoever signs in next.
      localStorage.removeItem(PARKED_REFRESH_KEY);
      window.location.assign(idleTimedOut ? '/login?reason=idle_timeout' : '/login');
    }
    // Callers get the response BODY (not the axios error), which is the
    // long-standing contract here — hence the `err?.detail` idiom throughout the
    // app. Attach the HTTP status when the body is an object so callers can
    // branch on it (e.g. 409 conflict) instead of pattern-matching the message
    // text. Additive and non-enumerable, so existing consumers and anything
    // that serialises the body are unaffected.
    const body = (error.response && error.response.data) || 'Wrong Services';
    if (body && typeof body === 'object' && !('status' in body)) {
      Object.defineProperty(body, 'status', {
        value: error.response.status,
        enumerable: false,
        configurable: true
      });
    }
    return Promise.reject(body);
  }
);

// ---------------- Silent token refresh ----------------
// Two cooperating mechanisms keep the session alive:
//
//   1. Proactive refresh — scheduleProactiveRefresh() reads the JWT `exp`
//      claim and fires a refresh ~60 s before the token would expire. In
//      normal use the user never sees a 401 at all.
//
//   2. Reactive refresh — the response interceptor catches a 401 and
//      retries the original request after a fresh token is obtained. This
//      is the fallback when the proactive timer didn't run (tab was sleeping,
//      laptop was suspended, etc).
//
// When multiple requests 401 at the same time we only want ONE refresh call;
// the others wait for that promise and all retry with the fresh token.
let refreshPromise = null;
let proactiveRefreshTimer = null;

/* Where the actor's own refresh token waits while they are impersonating
 * somebody. JWTContext parks it on the way in and restores it on the way out;
 * the 401 handler below restores it when an impersonation token dies of old age
 * instead. Exported rather than written out twice, because two copies of a
 * localStorage key are two chances to typo one of them. */
export const PARKED_REFRESH_KEY = 'impersonationParkedRefreshToken';

/* True when the stored access token is an impersonation token. */
function isImpersonationToken() {
  try {
    return Boolean(jwtDecode(localStorage.getItem('serviceToken'))?.imp);
  } catch {
    return false;
  }
}

// How long before token expiry to kick off a refresh. 60 s is comfortably
// longer than a slow refresh round-trip, and short enough that a paused tab
// resuming usually still has time to refresh before the token actually dies.
const PROACTIVE_REFRESH_LEAD_MS = 60 * 1000;

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      // Retry up to 3 times on transient network errors so a brief blip
      // (Wi-Fi handoff, backend restart, etc) doesn't log the user out.
      // We only retry network/5xx errors — a 401 or 403 means the refresh
      // token itself is invalid and we should fail fast.
      let lastError = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const { data } = await axios.post(
            `${axiosServices.defaults.baseURL || ''}/api/v1/auth/refresh`,
            { refresh_token: refreshToken },
            { timeout: 15000 }
          );
          const newAccess = data?.access_token;
          const newRefresh = data?.refresh_token;
          if (newAccess) localStorage.setItem('serviceToken', newAccess);
          if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
          // Schedule the next proactive refresh against the new token's exp.
          scheduleProactiveRefresh();
          return newAccess || null;
        } catch (err) {
          lastError = err;
          const status = err?.response?.status;
          // Fail fast on real auth failures — retrying won't help.
          if (status === 401 || status === 403) throw err;
          // Backoff before next attempt (500ms, 1s).
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)));
          }
        }
      }
      throw lastError;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Read the current access token's `exp` claim and schedule a refresh shortly
// before it would expire. Safe to call repeatedly — it cancels any existing
// timer before scheduling a new one. No-op if there's no token, or if the
// token's already expired (the reactive 401 path will handle that case).
export function scheduleProactiveRefresh() {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }

  const accessToken = localStorage.getItem('serviceToken');
  const refreshToken = localStorage.getItem('refreshToken');
  if (!accessToken || !refreshToken) return;

  let exp;
  try {
    exp = jwtDecode(accessToken)?.exp;
  } catch {
    return;
  }
  if (!exp) return;

  const msUntilExpiry = exp * 1000 - Date.now();
  // If we're already past the lead window, fire immediately. setTimeout(0)
  // pushes the refresh to the next tick so the caller (typically a login or
  // init handler) finishes its work before the network call goes out.
  const refreshIn = Math.max(0, msUntilExpiry - PROACTIVE_REFRESH_LEAD_MS);

  proactiveRefreshTimer = setTimeout(() => {
    refreshAccessToken().catch((err) => {
      // Proactive refresh failed; reactive 401 retry will handle it on the
      // next API call. Surface for diagnostics only.
      console.warn('[Auth] Proactive refresh failed:', err?.message);
    });
  }, refreshIn);
}

// Cancel any pending proactive refresh — call on logout to stop the timer
// from re-acquiring a token after the user has signed out.
export function cancelProactiveRefresh() {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

export default axiosServices;

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
};

export const fetcherPost = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.post(url, { ...config });

  return res.data;
};
